import type {
  ChatHistoryAttachment,
  ChatHistoryMessage,
  ChatHistoryMessageKind,
} from '@choco-admin/types';
import { formatDateTime } from '@choco-admin/utils';
import { Card, CardBody, Chip, Image, Link } from '@heroui/react';
import React from 'react';
import type { IconType } from 'react-icons';
import { FiImage, FiMessageSquare, FiPackage } from 'react-icons/fi';

import { OrderDetailsPanel } from './OrderDetailsPanel';

interface ChatHistoryMessageItemProps {
  message: ChatHistoryMessage;
}

const IMAGE_EXTENSION_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;

const KIND_ICON: Record<ChatHistoryMessageKind, IconType> = {
  order: FiPackage,
  media: FiImage,
  plain: FiMessageSquare,
};

const KIND_ICON_COLOR: Record<ChatHistoryMessageKind, string> = {
  order: 'text-primary',
  media: 'text-warning',
  plain: 'text-secondary',
};

const MediaAttachment: React.FC<{ attachment: ChatHistoryAttachment }> = ({
  attachment,
}) => {
  const isImage = IMAGE_EXTENSION_PATTERN.test(attachment.url);

  return (
    <div className="space-y-2">
      {isImage && (
        <Image
          src={attachment.url}
          alt="Message attachment"
          className="max-h-48 rounded-medium border border-default-200 object-contain"
        />
      )}
      <Link href={attachment.url} isExternal showAnchorIcon size="sm">
        View attachment
      </Link>
    </div>
  );
};

/**
 * Renders a single chat history message. Plain, media, and order messages are
 * visually distinct: each has its own icon, and media messages surface the
 * attachment while order messages surface the operational order details.
 */
export const ChatHistoryMessageItem: React.FC<ChatHistoryMessageItemProps> = ({
  message,
}) => {
  const { kind, attachment, order } = message;

  const KindIcon = KIND_ICON[kind];
  const iconColorClass = KIND_ICON_COLOR[kind];

  return (
    <Card className="shadow-sm" data-testid={`chat-message-${kind}`}>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <KindIcon className={`h-4 w-4 ${iconColorClass}`} aria-hidden />
            <span className="font-medium text-foreground">{message.from}</span>
            <span className="text-default-500">→</span>
            <span className="text-default-600">{message.to}</span>
          </div>

          <div className="flex items-center gap-2">
            {kind === 'media' && (
              <Chip size="sm" variant="flat" color="warning">
                Media
              </Chip>
            )}
            {kind === 'order' && order && (
              <Chip size="sm" variant="flat" color="primary">
                {order.referenceId
                  ? `Order ${order.referenceId}`
                  : `Order #${order.id}`}
              </Chip>
            )}
            <span className="text-sm text-default-500">
              {formatDateTime(message.dateTime)}
            </span>
          </div>
        </div>

        {message.message && (
          <div className="whitespace-pre-wrap text-default-700">
            {message.message}
          </div>
        )}

        {kind === 'media' && attachment && (
          <MediaAttachment attachment={attachment} />
        )}

        {kind === 'order' && order && <OrderDetailsPanel order={order} />}
      </CardBody>
    </Card>
  );
};
